/**
 * @fileoverview 데이터 패칭을 위한 범용 커스텀 훅
 * @description 마운트 시 자동 호출, 로딩/에러/데이터 상태 관리, 수동 refetch 지원
 *
 * 기존 useApiRequest와의 차이점:
 * - useApiRequest: 수동 execute() 호출 방식, 토스트 알림 지원, 뮤테이션에 적합
 * - useDataFetch: 마운트 시 자동 호출(immediate), 순수 데이터 조회에 최적화
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useDataFetch 훅 옵션
 * @interface UseDataFetchOptions
 * @template T - 데이터 타입
 */
interface UseDataFetchOptions<T> {
  /** 마운트 시 자동 호출 여부 (기본값: true) */
  immediate?: boolean;
  /** 초기 데이터 값 */
  initialData?: T;
  /** 성공 시 콜백 함수 */
  onSuccess?: (data: T) => void;
  /** 실패 시 콜백 함수 */
  onError?: (error: Error) => void;
}

/**
 * useDataFetch 훅 반환 타입
 * @interface UseDataFetchReturn
 * @template T - 데이터 타입
 */
interface UseDataFetchReturn<T> {
  /** 패칭된 데이터 */
  data: T | null;
  /** 로딩 상태 */
  loading: boolean;
  /** 에러 객체 */
  error: Error | null;
  /** 데이터 수동 재조회 함수 */
  refetch: () => Promise<void>;
}

/**
 * 데이터 패칭을 위한 범용 커스텀 훅
 *
 * @description
 * - 컴포넌트 마운트 시 자동으로 데이터를 조회 (immediate 옵션으로 제어)
 * - 로딩, 에러, 데이터 상태를 자동 관리
 * - refetch()를 통한 수동 재조회 지원
 * - 언마운트 시 상태 업데이트 방지
 *
 * @example
 * ```tsx
 * // 기본 사용 - 마운트 시 자동 조회
 * const { data, loading, error, refetch } = useDataFetch(
 *   () => fetchUserList()
 * );
 *
 * // 자동 조회 비활성화
 * const { data, refetch } = useDataFetch(
 *   () => fetchUserById(userId),
 *   { immediate: false }
 * );
 *
 * // 초기 데이터와 콜백 사용
 * const { data } = useDataFetch(
 *   () => fetchConfig(),
 *   {
 *     initialData: defaultConfig,
 *     onSuccess: (config) => console.log('설정 로드 완료:', config),
 *     onError: (err) => toast.error('설정 로드 실패')
 *   }
 * );
 * ```
 *
 * @template T - 패칭할 데이터의 타입
 * @param {function} fetchFn - 데이터를 반환하는 비동기 함수
 * @param {UseDataFetchOptions<T>} options - 훅 옵션
 * @returns {UseDataFetchReturn<T>} 데이터 패칭 상태와 제어 함수
 */
export function useDataFetch<T>(
  fetchFn: () => Promise<T>,
  options: UseDataFetchOptions<T> = {}
): UseDataFetchReturn<T> {
  const {
    immediate = true,
    initialData = null,
    onSuccess,
    onError
  } = options;

  const [data, setData] = useState<T | null>(initialData ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 언마운트 후 상태 업데이트 방지
  const mountedRef = useRef(true);
  // fetchFn의 최신 참조 유지 (useEffect 의존성 문제 방지)
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // 콜백의 최신 참조 유지
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchFnRef.current();

      if (mountedRef.current) {
        setData(result);
        onSuccessRef.current?.(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        const normalizedError = err instanceof Error ? err : new Error(String(err));
        setError(normalizedError);
        console.error('데이터 조회 실패:', normalizedError);
        onErrorRef.current?.(normalizedError);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (immediate) {
      refetch();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [immediate, refetch]);

  return {
    data,
    loading,
    error,
    refetch
  };
}

export type { UseDataFetchOptions, UseDataFetchReturn };
export default useDataFetch;
